import { alpha3ToAlpha2, alphaToAlias } from '@/components/common/CountryInput';
import Drawer from '@/components/common/Drawer';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import { ArrowCircleDown } from '@/components/common/icons';
import { BigHeader, Paragraph } from '@/components/common/Typography';
import { VGSProvider } from '@/components/common/VGSProvider';
import { IdvForm } from '@/components/idv/IdvForm';
import {
    AvoidIndustriesView,
    DownturnReactionView,
    InvestmentPriorityView,
    SupportedValuesView,
} from '@/components/idv/views';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { useUserData } from '@/services/user';
import { transparentize } from '@/util/chakra';
import { Box, Text, useTheme } from '@chakra-ui/react';
import {
    EMPLOYMENT_INDUSTRY_MAP,
    EMPLOYMENT_ROLE_MAP,
} from '@common/employment';
import {
    AVOID_INDUSTRY_OPTIONS,
    DOWNTURN_REACTION_OPTIONS,
    formatPhone,
    INVESTMENT_PRIORITY_OPTIONS,
    SUPPORTED_VALUES_OPTIONS,
} from '@common/inputs';
import { UserInfo } from '@common/react/user';
import { toDollars } from '@common/util/currency';
import { stringLiteralToTitleCase } from '@common/util/strings';
import { isNotNullish, isNumber } from '@common/util/types';
import { useEffect, useReducer, useRef, useState } from 'react';
import { AddressView } from './AddressView';
import { CitizenshipView } from './CitizenshipView';
import { EmployerAddressView } from './EmployerAddressView';
import { EmploymentStatusView } from './EmploymentStatusView';
import { ProfileView } from './ProfileView';

export const SummaryView = () => {
    useTrackPageViewed('IDV > SummaryView');

    const { userInfo } = useUserData();
    const [showSections, setShowSection] = useState(false);

    /**
     * This rerenders the Accordion sections after page load since their heights are determined dynamically
     */
    useEffect(() => setShowSection(true), []);

    return (
        <IdvForm
            transition="all 600ms ease-out"
            initValues={() => ({})}
            toMutationInput={() => ({
                summaryPageFinished: true,
            })}
            backNextNavProps={{
                nextButtonText: 'Looks good',
                onClickEventParams: {
                    location: 'IDV > SummaryView',
                },
            }}
        >
            <BigHeader mb={4}>
                You’re done. Let’s review your details.
            </BigHeader>
            <Text color="textMuted" fontSize="md" mb={4}>
                Our custodian, BNY Mellon Pershing, typically reviews
                applications within 2-3 business days. We do not sell your data
                and we use bank-level encryption to keep your data safe.
            </Text>
            {!!userInfo && (
                <FadeInBox animate={{ opacity: showSections ? 1 : 0 }}>
                    {getOnboardingSections(userInfo).map(
                        ({ title, ...props }) => (
                            <AccordionSection
                                key={title}
                                title={title}
                                {...props}
                            />
                        )
                    )}
                </FadeInBox>
            )}
        </IdvForm>
    );
};

type AccordionSection = {
    title: string;
    view?: React.ReactNode;
    fields: {
        label: string;
        value?: React.ReactNode;
        view?: React.ReactNode;
    }[];
    isOpen?: boolean;
    isAccordion?: boolean;
};

const AccordionSection = ({
    title,
    view,
    fields,
    isOpen: _isOpen,
    isAccordion = true,
}: AccordionSection) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(_isOpen);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const contentHeight = contentRef.current?.getBoundingClientRect()?.height;
    const hoverBgColor = transparentize('gray.500', 0.05)(theme);

    useEffect(() => forceUpdate(), [fields]);

    return (
        <FlexRow flexDir="column" userSelect="none">
            <FlexRow
                alignItems="center"
                justifyContent="space-between"
                py={3}
                cursor={isAccordion ? 'pointer' : 'default'}
                position="relative"
                onClick={() => (isAccordion ? setIsOpen(!isOpen) : {})}
            >
                {isAccordion && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        opacity={0}
                        bg={`linear-gradient(90deg, transparent 0%, ${hoverBgColor} 33%, ${hoverBgColor} 66%, transparent 100%)`}
                        transition="all 150ms ease-out"
                        _hover={{ opacity: 1 }}
                    />
                )}
                <FlexRow alignItems="center">
                    <Paragraph fontWeight="semibold" mt="2px">
                        {title}
                    </Paragraph>
                </FlexRow>
                {isAccordion && (
                    <Box
                        color={isOpen ? 'gray.700' : 'gray.400'}
                        transform={isOpen ? 'rotate(180deg)' : undefined}
                        transition="all 200ms ease-out"
                    >
                        <ArrowCircleDown />
                    </Box>
                )}
            </FlexRow>
            <FlexRow
                flexDir="column"
                borderBottom={isAccordion ? '1px solid transparent' : undefined}
                borderColor="border"
                transition="all 200ms ease-out"
                mt={isOpen ? 1 : undefined}
                h={
                    isAccordion
                        ? isOpen
                            ? `${(contentHeight || 0) + 12}px`
                            : 0
                        : undefined
                }
                overflow={isAccordion ? 'hidden' : undefined}
                position="relative"
            >
                <FlexRow
                    ref={contentRef}
                    flexDir="column"
                    {...(isAccordion
                        ? {
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                          }
                        : undefined)}
                >
                    {fields.map(
                        ({ label, value, view: fieldView }) =>
                            !!value && (
                                <FlexRow
                                    key={label}
                                    justifyContent="space-between"
                                    py={1}
                                >
                                    <Paragraph
                                        flex="0 0 auto"
                                        color="grayText"
                                        mr={6}
                                    >
                                        {label}
                                    </Paragraph>
                                    <SectionDrawer view={fieldView || view}>
                                        {value}
                                    </SectionDrawer>
                                </FlexRow>
                            )
                    )}
                </FlexRow>
            </FlexRow>
        </FlexRow>
    );
};

const SectionDrawer = ({
    children,
    view,
}: {
    children: React.ReactNode;
    view?: React.ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <Paragraph
                borderBottom="1px solid transparent"
                borderColor="border"
                cursor="pointer"
                transition="all 150ms ease-out"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                _hover={{
                    borderColor: 'text',
                }}
                onClick={() => setIsOpen(true)}
            >
                {children}
            </Paragraph>
            <Drawer
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                size="lg"
                trapFocus={false}
            >
                <FlexRow h="100%" flexDir="column" p={24}>
                    {view}
                </FlexRow>
            </Drawer>
        </>
    );
};

const getOnboardingSections = (userInfo: UserInfo): AccordionSection[] => {
    const onboardingData = userInfo?.onboardingData;
    const employerAddress = onboardingData?.employerAddress;

    const sections: AccordionSection[] = [
        {
            title: 'About you',
            view: <ProfileView />,
            fields: [
                {
                    label: 'Your legal name',
                    value: `${userInfo.firstName || ''} ${
                        userInfo.lastName || ''
                    }`.trim(),
                },
                {
                    label: 'Your phone number',
                    value: userInfo.phone
                        ? formatPhone(userInfo.phone)
                        : undefined,
                },
                {
                    label: 'Your preferred name',
                    value: userInfo.preferredName,
                },
                {
                    label: 'Your birthday',
                    value: userInfo.dob
                        ?.split('-')
                        .sort((a, b) => a.length - b.length)
                        .join('/'),
                },
                {
                    label: 'Your address',
                    value: [
                        userInfo.address1,
                        userInfo.address2,
                        userInfo.locality,
                        userInfo.region,
                        userInfo.postalCode,
                    ]
                        .filter(isNotNullish)
                        .join(', '),
                    view: <AddressView />,
                },
            ],
            isOpen: true,
        },
        {
            title: 'Citizenship',
            view: (
                <VGSProvider>
                    <CitizenshipView />
                </VGSProvider>
            ),
            fields: [
                {
                    label: 'Country',
                    value: onboardingData?.country && (
                        <CountryLabel alpha3={onboardingData.country} />
                    ),
                },
                {
                    label: 'US tax status',
                    value: stringLiteralToTitleCase(
                        onboardingData?.residenceStatus
                    ),
                },
            ],
        },
        {
            title: 'Your work',
            view: <EmploymentStatusView />,
            fields: [
                {
                    label: 'Employment status',
                    value: stringLiteralToTitleCase(
                        onboardingData?.employmentStatus
                    ),
                },
                {
                    label: 'Annual income',
                    value: isNumber(userInfo.annualIncome)
                        ? toDollars(userInfo.annualIncome)
                        : undefined,
                },
                {
                    label: 'Employer name',
                    value: onboardingData?.employerName,
                },
                {
                    label: 'Employer industry',
                    value: onboardingData?.industry
                        ? EMPLOYMENT_INDUSTRY_MAP[onboardingData.industry]
                        : undefined,
                },
                {
                    label: 'Your role at employer',
                    value: onboardingData?.role
                        ? EMPLOYMENT_ROLE_MAP[onboardingData.role]
                        : undefined,
                },
                ...(employerAddress
                    ? [
                          {
                              label: 'Employer address',
                              value: [
                                  employerAddress?.address1,
                                  employerAddress?.address2,
                                  employerAddress?.locality,
                                  employerAddress?.region,
                                  employerAddress?.postalCode,
                              ]
                                  .filter(isNotNullish)
                                  .join(', '),
                              view: <EmployerAddressView />,
                          },
                      ]
                    : []),
            ],
        },
    ];

    sections.push({
        title: 'Your investor profile',
        fields: [
            {
                label: 'Your primary investment goal',
                value: INVESTMENT_PRIORITY_OPTIONS.find(
                    ({ value }) =>
                        value ===
                        userInfo.investmentProfile?.investment_priority
                )?.label,
                view: <InvestmentPriorityView />,
            },
            {
                label: 'If your portfolio value dropped',
                value: DOWNTURN_REACTION_OPTIONS.find(
                    ({ value }) =>
                        value === userInfo.investmentProfile?.downturn_reaction
                )?.label,
                view: <DownturnReactionView />,
            },
            {
                label: 'Industries to avoid',
                value:
                    userInfo.investmentProfile?.avoid_industries
                        ?.map(
                            (v) =>
                                AVOID_INDUSTRY_OPTIONS.find(
                                    ({ value }) => value === v
                                )?.label
                        )
                        .join(', ') || 'None chosen',
                view: <AvoidIndustriesView />,
            },
            {
                label: 'Values to support',
                value:
                    userInfo.investmentProfile?.supported_values
                        ?.map(
                            (v) =>
                                SUPPORTED_VALUES_OPTIONS.find(
                                    ({ value }) => value === v
                                )?.label
                        )
                        .join(', ') || 'None chosen',
                view: <SupportedValuesView />,
            },
        ],
    });

    return sections;
};

const CountryLabel = ({ alpha3 }: { alpha3: string }) => (
    <>
        <Box
            as="span"
            className={`fi fi-${alpha3ToAlpha2(alpha3)?.toLowerCase()}`}
            mr={2}
        />
        {alphaToAlias(alpha3)}
    </>
);
